import { isAddress, type Address } from "viem";

/**
 * El referido que trae la URL: `https://keellabs.app/?ref=0x…`.
 *
 * MANDA EL PRIMERO QUE LO TRAJO, NO EL ULTIMO. Si alguien llega con el enlace de A, se lo piensa una
 * semana, y vuelve con el de B, cuenta A. Es la regla que hace que compartir tenga sentido: el que
 * convence es el primero, y el ultimo enlace pulsado no puede quedarse con su trabajo. El contrato
 * hace cumplir lo mismo del otro lado (`YaTieneReferidor`), asi que esto no es la unica defensa.
 *
 * SE GUARDA ANTES DE CONECTAR LA WALLET. Quien llega por un enlace casi nunca tiene wallet conectada
 * todavia: si el dato solo se leyera al conectar, se perderia en el 90% de los casos. Por eso vive en
 * `localStorage` y espera ahi hasta que haya alguien a quien atribuirselo.
 *
 * LA URL SE LIMPIA. Deja el `?ref=` puesto y el usuario compartira ESA url —con el referido de otro
 * dentro— creyendo que comparte la suya. Ademas ensucia cualquier enlace que copie.
 */
const CLAVE = "keel.ref";

type Guardado = { ref: Address; visto: number };

export function capturaReferidoDeLaUrl() {
  try {
    const url = new URL(location.href);
    const crudo = url.searchParams.get("ref");
    if (!crudo) return;

    url.searchParams.delete("ref");
    history.replaceState(null, "", url.pathname + url.search + url.hash);

    if (!isAddress(crudo)) return;
    if (leeReferido()) return; // ya habia uno: manda el primero
    localStorage.setItem(
      CLAVE,
      JSON.stringify({ ref: crudo.toLowerCase() as Address, visto: Date.now() } satisfies Guardado)
    );
  } catch {
    // localStorage puede estar bloqueado (modo privado, permisos). No es motivo para romper la app:
    // sin referido guardado todo lo demas funciona igual.
  }
}

export function leeReferido(): Address | null {
  try {
    const crudo = localStorage.getItem(CLAVE);
    if (!crudo) return null;
    const d = JSON.parse(crudo) as Guardado;
    return isAddress(d.ref) ? (d.ref.toLowerCase() as Address) : null;
  } catch {
    return null;
  }
}

export function olvidaReferido() {
  try {
    localStorage.removeItem(CLAVE);
  } catch {
    /* ver arriba */
  }
}

/** El sitio público. Lo que se comparte tiene que llevar SIEMPRE esto delante. */
export const SITIO = "https://keellabs.app";

/**
 * El enlace que reparte quien invita.
 *
 * EL DOMINIO ES FIJO, NO `location.origin`. Con el origen de la pestaña, el enlace copiado desde una
 * preview, desde `localhost` o desde una IP salía con ESE host dentro — `http://localhost:5173/?ref=0x…`—
 * y se comparte igual de bien que uno bueno: no falla al copiarlo, falla en el móvil de la otra
 * persona, que es donde ya no se ve. Se conserva el origen solo si de verdad estamos en el sitio
 * público (incluye sus subdominios), para no romper un despliegue futuro en otro dominio propio.
 */
export function enlaceInvitacion(referidor: Address | string) {
  let base = SITIO;
  try {
    if (typeof location !== "undefined" && /(^|\.)keellabs\.app$/i.test(location.hostname)) {
      base = location.origin;
    }
  } catch { /* sin `location` (SSR, pruebas): se queda el sitio público */ }
  return `${base}/?ref=${String(referidor).toLowerCase()}`;
}
